import { useState, useEffect } from 'react';
import { FaAngleUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';

export const ButtonLink = ({ to, children, external = false, className = '' }) => (
    <Link
        to={to}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className={`font-display px-4 py-2 gradient-button text-white rounded-xl shadow-2xl
        hover:shadow-xl hover:scale-105 transform transition duration-300 ease-in-out
        hover:ring-8 ring-white/20 whitespace-nowrap text-[0.8rem] sm:text-sm md:text-lg ${className}`}
    >
        {children}
    </Link>
);

export const InfoCard = ({ icon, title, children }) => (
    <div className="flex flex-col gap-4 justify-center items-center w-full md:w-80 lg:w-72 xl:w-80 bg-white rounded-3xl py-8 shadow-2xl">
        <img src={icon} alt="" className="w-24 h-24" />
        {title && (
            <h3 className="font-display font-black text-gray-800 text-base sm:text-xl md:text-2xl xl:text-3xl">
                {title}
            </h3>
        )}
        <p className="font-display font-medium text-black text-xs sm:text-sm md:text-lg lg:text-sm xl:text-base px-6">
            {children}
        </p>
    </div>
);

export const StepBadge = ({ number, background }) => {
    const backgroundImage = background || '/assets/images/artifacts/lepesek_desktop.svg';
    return (
        <div
            className="w-[300px] h-[300px] md:w-[340px] md:h-[340px] inline-flex items-center justify-center bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${backgroundImage})` }}
        >
            <div className="text-center">
                <p className="font-display font-bold text-8xl md:text-5xl lg:text-6xl xl:text-8xl">
                    {number}.
                </p>
                <p className="font-display font-bold text-5xl md:text-2xl lg:text-3xl xl:text-5xl">
                    lépés
                </p>
            </div>
        </div>
    );
};

export const StepCard = ({ icon, title, children, actions }) => (
    <div className="flex flex-col gap-4 justify-center items-center w-[320px] md:w-[640px] bg-white rounded-3xl p-6 shadow-2xl text-center">
        {icon && <img src={icon} alt="" className="size-24" />}
        {title && (
            <h3 className="font-display font-black text-lg md:text-xl lg:text-2xl xl:text-3xl text-gray-800">
                {title}
            </h3>
        )}
        <div className="text-black text-xs md:text-sm">{children}</div>
        {actions && <div className="flex justify-center gap-4">{actions}</div>}
    </div>
);

export const StepRow = ({ number, reverse = false, children, background }) => (
    <div
        className={`flex flex-col ${
            reverse ? 'md:flex-row-reverse' : 'md:flex-row'
            } justify-center items-center gap-4`}
    >
        <StepBadge number={number} background={background} />
        {children}
    </div>
);

const above18Steps = [
    {
        number: 1,
        icon: '/assets/images/doq_landing/6.svg',
        title: 'Töltsd le a DOQDrive Next applikációt!',
        content: 'Android esetén a Google Play-ből, iOS esetén az App Store-ból.',
        actions: (
            <>
                <ButtonLink
                    to="https://play.google.com/store/apps/details?id=com.doqdrivemobilern"
                    external
                    className="gradient-blue rounded-lg ring-black/20"
                >
                    Google Play
                </ButtonLink>

                <ButtonLink
                    to="https://apps.apple.com/hu/app/doqdrive-next/id1626961295"
                    external
                    className="gradient-blue rounded-lg ring-black/20"
                >
                    App Store
                </ButtonLink>
            </>
        ),
    },
    {
        number: 2,
        reverse: true,
        icon: '/assets/images/doq_landing/7.svg',
        title: 'Regisztrálj az App-ba és kövesd az utasításokat!',
        content: (
            <div className="text-start">
                Amire szükséged lesz a regisztráció során:
                <br />
                <br />
                <ul className="list-disc">
                    <li>Kattints a regisztráció gombra és kövesd az utasításokat.</li>
                    <li>Figyelj, hogy a nevedet úgy add meg, ahogyan a személyi igazolványodban is szerepel.</li>
                    <li>Írd be sorban az adataidat, és add meg az ellenőrző kódot is, amit SMS-ben küldenek neked.</li>
                    <li>Válaszd ki, hogy személyi igazolvánnyal vagy útlevéllel fogod igazolni magad, és fotózd be azt a telefonoddal. Személyinél a hátoldalt is be kell fotózni.</li>
                    <li>A mozgásfelismerés során kövesd a képernyőn megjelenő utasításokat.</li>
                    <li>A képeket a rendszer várhatóan 5 percen belül hitelesíti.</li>
                </ul>
                <br/>
                Ha a regisztrációs folyamatban elakadtál vagy hibát észleltél, akkor fordulj a DOQ Drive
                Support csapatához.
                <br/>
                <br/>
                DOQ Drive Support
                <br/>
                Hétköznapokon: 8.00 – 17.00 óráig
                <br/>
                Tel.: <Link to="tel:+36703110864" className='hover:underline'>+36 70 311 08 64</Link>
                <br/>
                E-mail: <Link to="mailto:support@doqdrive.com" className='hover:underline'>support@doqdrive.com</Link>
            </div>
        ),
    },
    {
        number: 3,
        icon: '/assets/images/doq_landing/8.svg',
        content: 'Ha minden rendben lezajlott, akkor kapsz egy e-mailt, ami tartalmazni fogja a DOQ ID-t. Az e-mailes vagy SMS kóddal történő megerősítés után már be tudsz lépni a DOQDrive-ba.',
    },
    {
        number: 4,
        reverse: true,
        icon: '/assets/images/doq_landing/9.svg',
        content: 'Ezt követően a Fürge Diák felé is jelezned kell, hogy készen állsz a digitális ügyintézésre. Ehhez jelentkezz be a furgediak.hu weboldalon, a szerződéskötés és digitális ügyintézés blokkban add meg a DOQDrive azonosítódat és nyilatkozz az elektronikus eljárások elfogadásáról.',
    },
    {
        number: 5,
        icon: '/assets/images/doq_landing/10.svg',
        content: 'A DOQ azonosító néhány percen belül beérkezik a Fürge Diák rendszerébe, és ettől a pillanattól tudsz digitálisan aláírni Fürgés diákmunkához kapcsolódó dokumentumokat. Elakadtál a digitális aláírás folyamatában? Kattints és részletesen megmutatjuk hogyan tudsz pár lépésben elektronikusan dokumentumot aláírni és beküldeni Iskolaszövetkezetünknek!',
        actions: (
            <ButtonLink
                to="/digitalis-ugyintezes-utmutato-diakoknak"
                className="gradient-blue rounded-lg ring-black/20"
            >
                Tovább
            </ButtonLink>
        ),
    },
];

const below18InfoCards = [
    {
        icon: '/assets/images/doq_landing/4.svg',
        content: 'Törvényes képviselődnek regisztrálnia kell a DOQ rendszerébe, mint magánszemély. Ez történhet még az irodába érkezést megelőzően vagy az irodában is, de javasoljuk, hogy tegyétek meg otthon, mert akkor a fiók hitelesítésére nem kell várni.',
    },
    {
        icon: '/assets/images/doq_landing/5.svg',
        content: 'Ha szeretnél a Fürge Diáknál diákmunkát vállalni, első alkalommal mindenképp személyesen kell szerződést kötnöd valamelyik irodánkon, ezen az alkalmon jelen kell lennie a törvényes képviselődnek is, hiszen neki is alá kell írnia a szerződésedet. Ezt követően már önállóan válogathatsz a munkalehetőségeink között.',
    },
];

const below18Steps = [
    {
        number: 1,
        icon: '/assets/images/doq_landing/6.svg',
        title: 'Töltsétek le a DOQDrive Next applikációt!',
        content: 'Android esetén a Google Play-ből, iOS esetén az App Store-ból.',
        actions: (
            <>
                <ButtonLink
                    to="https://play.google.com/store/apps/details?id=com.doqdrivemobilern"
                    external
                    className="gradient-blue rounded-lg ring-black/20"
                >
                    Google Play
                </ButtonLink>

                <ButtonLink
                    to="https://apps.apple.com/hu/app/doqdrive-next/id1626961295"
                    external
                    className="gradient-blue rounded-lg ring-black/20"
                >
                    App Store
                </ButtonLink>
            </>
        ),
    },
    {
        number: 2,
        reverse: true,
        icon: '/assets/images/doq_landing/7.svg',
        title: 'Regisztráljatok az App-ba a törvényes képviselőd adataival!',
        content: (
            <div className="text-start">
                Amire szükséged lesz a regisztráció során:
                <br />
                <br />
                <ul className="list-disc">
                    <li>Törvényes képviselőd kattintson a regisztráció gombra és kövessétek az utasításokat.</li>
                    <li>Törvényes képviselőd a nevét úgy adja meg, ahogyan a személyi igazolványában is szerepel.</li>
                    <li>Írja be sorban az adatait, és adja meg az ellenőrző kódot is, amit SMS-ben küldenek neki.</li>
                    <li>Törvényes képviselőd válassza ki, hogy személyi igazolvánnyal vagy útlevéllel fogja igazolni magát, és fotózza be azt a telefonnal. Személyinél a hátoldalt is be kell fotózni.</li>
                    <li>A mozgásfelismerés során kövesse a képernyőn megjelenő utasításokat. A képeken a törvényes képviselődnek kell szerepelni.</li>
                    <li>A képeket a rendszer várhatóan 5 percen belül hitelesíti.</li>
                </ul>
                <br/>
                Ha a regisztrációs folyamatban elakadtál vagy hibát észleltél, akkor fordulj a DOQ Drive Support csapatához.
                <br/>
                <br/>
                DOQ Drive Support
                <br/>
                Hétköznapokon: 8.00 – 17.00 óráig
                <br/>
                Tel.: <Link to="tel:+36703110864" className='hover:underline'>+36 70 311 08 64</Link>
                <br/>
                E-mail: <Link to="mailto:support@doqdrive.com" className='hover:underline'>support@doqdrive.com</Link>
            </div>
        ),
    },
    {
        number: 3,
        icon: '/assets/images/doq_landing/8.svg',
        content: 'Ha minden rendben lezajlott, akkor kaptok egy e-mailt, ami tartalmazni fogja a DOQ ID-t, ami az előzőleg megadott telefonszámod. Ezután már be tudtok lépni a DOQDrive-ba.',
    },
    {
        number: 4,
        reverse: true,
        icon: '/assets/images/doq_landing/9.svg',
        content: 'Ezt követően a Fürge Diák felé jeleznetek kell, hogy készen álltok a digitális ügyintésére. Ehhez a DOQ Drive rendszerébe regisztrált törvényes képviselőddel együtt be kell fáradnotok a hozzátok legközelebbi Fürge Diák irodába.',
    },
    {
        number: 5,
        icon: '/assets/images/doq_landing/10.svg',
        content: 'Irodánkon a törvényes képviselőddel együtt személyesen nyilatkoztok az elektronikus eljárásokhoz szükséges feltételek elfogadásáról. Ezt követően a DOQ azonosítódat kollégáink rögzítik a Fürge Diák rendszerébe. Ettől a pillanattól törvényes képviselőd digitálisan is tud aláírni Fürgés diákmunkához kapcsolódó dokumentumot. Elakadtál a digitális aláírás folyamatában? Kattints és részletesen megmutatjuk hogyan tudsz pár lépésben elektronikusan dokumentumot aláírni és beküldeni Iskolaszövetkezetünknek!',
        actions: (
            <ButtonLink
                to="/digitalis-ugyintezes-utmutato-diakoknak"
                className="gradient-blue rounded-lg ring-black/20"
            >
                Tovább
            </ButtonLink>
        ),
    },
];

export const SocialCard = ({ to, icon, children, hoverColor }) => {
    const hoverClass = hoverColor === 'instructor' ? 'hover:bg-gradient-to-r hover:from-orange-500 hover:to-orange-400' : 'hover:bg-gradient-to-r hover:from-yellow-700 hover:to-yellow-600';
    return (
        <Link
            to={to}
            target="_blank"
            rel="noopener noreferrer"
            className={`group flex flex-col gap-4 justify-center items-center w-32 md:w-64 h-32 md:h-64 bg-white rounded-3xl
                text-gray-800 transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-xl
                hover:ring-8 ring-white/20 hover:text-white ${hoverClass}`}
        >
            <img
                src={icon}
                alt=""
                className="w-12 md:w-24 h-12 md:h-24 transition-all duration-300 group-hover:brightness-0 group-hover:invert"
            />
            <span className="font-display font-bold text-sm md:text-2xl">
                {children}
            </span>
        </Link>
    )
};

export const CollapseButton = ({ onClick, buttonColor }) => {
    const buttonClass = buttonColor === 'instructor' ? 'gradient-button-instructor' : 'gradient-button';
    return (
        <button
            className={`flex items-center justify-center font-display text-sm md:text-lg px-4 py-2 ${buttonClass} text-white rounded-lg 
                shadow-2xl hover:shadow-2xl hover:scale-105 transform transition duration-300 ease-in-out hover:ring-8 ring-white/20`}
            onClick={onClick}
        >
            <FaAngleUp className="size-6" />
            Összecsuk
        </button>
    );
};

export const HighlightText = ({ children, className = '', path }) => {
    const backgroundImagePath = path || '/assets/images/artifacts/felszabadult_idot.svg'; 
    return (
        <span
            className={`relative inline-block ${className}`}
            style={{
                backgroundImage: `url(${backgroundImagePath})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                padding: '5px 10px',
                display: 'inline-block',
            }}
        >
            {children}
        </span>
    );
};

export const PhoneVideoMockup = ({ videoSrc }) => (
    <div className="relative w-[280px] md:w-[340px]">
        <video
            src={videoSrc}
            loop
            playsInline
            controls
            className="absolute left-[4%] top-[1.5%] w-[92%] h-[97%] rounded-[42px] bg-black"
        />

        <img
            src="/assets/images/mobile.svg"
            alt="iPhone mockup"
            className="relative z-10 w-full pointer-events-none"
        />
    </div>
);

export default function DigitalAdministrationPage() {
    const [openSection, setOpenSection] = useState(null); // For track the button that is selected

    const handleSectionToggle = (section) => {
        // Change between sections
        setOpenSection(section);
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }, []);

    return (
        <div className="gradient-blue min-h-screen">
            <div
                className={'w-full flex flex-col min-h-screen'} 
                style={{
                    backgroundImage: 'url(/assets/images/background/pattern-opacity.png)',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center top',
                }}
            >
                <div className='flex flex-col gap-8 container mx-auto px-site py-site'> 
                    <div className="w-full text-center">
                        {/* Header Section */}
                        <div className="flex flex-col xl:flex-row gap-4 justify-center">
                            {/* Left Section: Text */}
                            <div className="flex flex-col justify-center items-center gap-8">
                                <h1 
                                    className="font-display font-bold text-2xl sm:text-4xl md:text-5xl whitespace-nowrap "
                                >
                                    Sok idődet elveszi
                                    <br />
                                    az ügyintézések miatti{' '}
                                    <br />
                                    <HighlightText>utazás és várakozás?</HighlightText>
                                </h1>
                                <b className="font-display font-black text-white block text-xs sm:text-lg md:text-xl">
                                    Digitális megoldásával időt spórol neked a Fürge Diák!
                                </b>
                                <p className="font-display text-white max-w-md mx-auto text-[0.7rem] sm:text-sm md:text-lg">
                                    Pár lépés megtétele után a Fürge Diákkal személyes megjelenés nélkül,
                                    <br/>
                                    bárhonnan szerződhetsz digitálisan. Sőt, az eseti megállapodásokat is
                                    <br/>
                                    aláírhatod, és a jogviszony igazolásokat is küldheted digitálisan.
                                    <br/>
                                    <br/>
                                    A DOQDrive Next applikációját választottuk a digitális hitelesítésre, a
                                    <br/>
                                    segítségével kiváltjuk a papír alapú dokumentumok személyes aláírását.
                                </p>
                                {/* Registration Button */}
                                <div
                                    className="font-display px-4 py-2 gradient-button text-white rounded-xl shadow-2xl hover:shadow-xl 
                                                hover:scale-105 transform transition duration-300 ease-in-out hover:ring-8 ring-white/20
                                                whitespace-nowrap text-[0.7rem] sm:text-sm md:text-lg"
                                >
                                    <Link
                                        to={'https://doqdrive.com/doqdrive-maganembereknek/'}
                                        target='_blank'
                                    >
                                        DOQDrive regisztráció
                                    </Link>
                                </div>
                            </div>

                            {/* Right Section: Image */}
                            <div className="flex justify-center items-center">
                                <img
                                    src="/assets/images/bgPicture.png"
                                    alt="Background Picture"
                                    className="size-[320px] md:size-[560px] object-contain"
                                />
                            </div>
                        </div>

                        {/* Cards Section */}
                        <div className="flex flex-col lg:flex-row gap-8 items-center lg:items-start justify-center px-4">
                            {/* Card 1 */}
                            <InfoCard icon="/assets/images/doq_landing/1.svg" title="Miért jó ez nekem?">
                                A telefonod segítségével bármikor
                                <br/>
                                és bárhonnan létrehozhatsz
                                <br/>
                                dokumentumot, utazási időt és
                                <br/>
                                várakozást spórolhatsz meg. A
                                <br/>
                                dokumentumok a DOQDrive
                                <br/>
                                hitelesítést követően pont olyanok,
                                <br/>
                                mint a személyesen aláírt
                                <br/>
                                dokumentumok.
                            </InfoCard>

                            {/* Card 2 */}
                            <InfoCard icon="/assets/images/doq_landing/2.svg" title="Használhatom ezt?">
                                A DOQDrive Next app használatával
                                <br/>
                                Fürge Diákos dokumentumokat
                                <br/>
                                digitálisan aláírni csak akkor tudsz,
                                <br/>
                                ha regisztrált Tag vagy a
                                <br/>
                                furgediak.hu weboldalon. Jelenleg
                                <br/>
                                csak magyar állampolgárok számára
                                <br/>
                                érhető el ez a lehetőség.
                            </InfoCard>

                            {/* Card 3 */}
                            <InfoCard icon="/assets/images/doq_landing/3.svg" title="Mennyibe kerül nekem?">
                                A DOQDrive Next app letöltése és a 
                                <br/>
                                regisztráció is ingyenes.
                            </InfoCard>
                        </div>
                    </div>

                    <div id='content' className="flex flex-col gap-4 w-full justify-center items-center text-center">
                        <h1 className="font-display font-bold text-2xl sm:text-4xl md:text-5xl">
                            <HighlightText>Nincs még</HighlightText>
                            <br/>
                            DOQDrive Next regisztrációd?
                        </h1>
                        <b className="text-white font-display font-black block text-xs sm:text-lg md:text-xl">
                            A folyamat nagyon egyszerű!
                        </b>
                        <p className="font-display text-white mt-4 md:mt-0 max-w-md mx-auto md:px-16 text-[0.7rem] sm:text-sm md:text-lg">
                            Nézd meg a videós útmutatónkat, vagy kattints életkorod szerinti gombra és már mutatjuk is a lépéseket!
                        </p>

                        <PhoneVideoMockup videoSrc="/assets/video/1.mp4" />

                        <div className="flex flex-col items-center md:flex-row md:justify-center gap-4">
                            {/* Above 18 Button */}
                            <button
                                className={`w-full md:w-1/2 font-display px-4 py-2 flex justify-center
                                            ${openSection === 'above18' ? 'gradient-yellow' : 'gradient-button'}
                                            text-white rounded-xl shadow-2xl whitespace-nowrap hover:ring-8 ring-white/20
                                            text-[0.7rem] sm:text-sm md:text-lg hover:shadow-lg
                                            transform transition duration-300 ease-in-out`}
                                onClick={() => handleSectionToggle('above18')}
                            >
                                Elmúltam már 18 éves
                            </button>

                            {/* Below 18 Button */}
                            <button
                                className={`w-full md:w-1/2 font-display px-4 py-2 flex justify-center
                                            ${openSection === 'below18' ? 'gradient-yellow' : 'gradient-button'}
                                            text-white rounded-xl shadow-2xl whitespace-nowrap hover:ring-8 ring-white/20
                                            text-[0.7rem] sm:text-sm md:text-lg hover:shadow-lg
                                            transform transition duration-300 ease-in-out`}
                                onClick={() => handleSectionToggle('below18')}
                            >
                                Nem múltam még el 18 éves
                            </button>
                        </div>

                        {/* Above 18 Section */}
                        <div
                            className={`overflow-hidden transition-all duration-1000 ease-in-out pb-8 ${
                                openSection === 'above18'
                                    ? 'max-h-fit opacity-100 translate-y-0'
                                    : 'max-h-0 opacity-0 -translate-y-4'
                            }`}
                        >
                            <div className="flex flex-col justify-center items-center gap-8">
                                {above18Steps.map((step) => (
                                    <StepRow key={step.number} number={step.number} reverse={step.reverse}>
                                        <StepCard icon={step.icon} title={step.title} actions={step.actions}>
                                            {step.content}
                                        </StepCard>
                                    </StepRow>
                                ))}

                                <ButtonLink
                                    to="https://doqdrive.com/doqdrive-maganembereknek/"
                                    external
                                    className="rounded-lg"
                                >
                                    DOQDrive regisztráció
                                </ButtonLink>

                                <CollapseButton 
                                    onClick={() => {
                                        setOpenSection(null);

                                        setTimeout(() => {
                                            document.getElementById('content')?.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'start',
                                            });
                                        }, 100);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Below 18 Section */}
                        <div
                            className={`overflow-hidden transition-all duration-1000 ease-in-out pb-8 ${
                                openSection === 'below18'
                                    ? 'max-h-fit opacity-100 translate-y-0'
                                    : 'max-h-0 opacity-0 -translate-y-4'
                            }`}
                        >
                            <div className="flex flex-col justify-center items-center gap-8">
                                <b className="font-display text-center font-black text-xs md:text-lg lg:text-xl text-white">
                                    18 éves kor alatt valamelyik törvényes képviselődnek (szülődnek) kell regisztrálnia a DOQ rendszerébe és egyetlen
                                    <br />
                                    alkalommal személyesen meg kell jelennetek az irodánkon.
                                </b>

                                <div className="w-full text-center">
                                    <div className="flex flex-col md:flex-row justify-center gap-4">
                                        {below18InfoCards.map((card) => (
                                            <div
                                                key={card.icon}
                                                className="flex flex-col gap-4 w-full md:w-[480px] md:h-fit bg-white rounded-3xl p-6 shadow-2xl"
                                            >
                                                <div className="flex justify-center">
                                                    <img src={card.icon} alt="" className="size-24" />
                                                </div>

                                                <p className="font-display text-black text-xs sm:text-sm md:text-lg lg:text-sm xl:text-base">
                                                    {card.content}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <p className="text-white font-display text-center font-medium text-xs md:text-base lg:text-lg">
                                    FONTOS, hogy az a szülő regisztráljon a DOQ Drive Next app-ba, aki el fog jönni veled az irodára szerződést aláírni és akinek nem teher az, ha ezt az
                                    <br />
                                    applikációt használni kell.
                                    <br />
                                    <br />
                                    Egy törvényes képviselő egy DOQ ID-val több 18 évnél fiatalabb diáknak is kezelheti a dokumentumait.
                                </p>

                                <b className="font-display text-center font-black text-xs md:text-lg lg:text-xl text-white">
                                    Amint a betöltöd a 18 éves kort, a szülői DOQ Drive Next fiók már nem alkalmazható aláírásra nálad.
                                    <br />
                                    Ekkor majd neked kell létrehoznod egy saját DOQ Drive Next fiókot (lásd 18 év felettiek folyamata).
                                </b>

                                {below18Steps.map((step) => (
                                    <StepRow key={step.number} number={step.number} reverse={step.reverse}>
                                        <StepCard icon={step.icon} title={step.title} actions={step.actions}>
                                            {step.content}
                                        </StepCard>
                                    </StepRow>
                                ))}

                                <ButtonLink
                                    to="https://doqdrive.com/doqdrive-maganembereknek/"
                                    external
                                    className="rounded-lg"
                                >
                                    DOQDrive regisztráció
                                </ButtonLink>

                                <CollapseButton 
                                    onClick={() => {
                                        setOpenSection(null);

                                        setTimeout(() => {
                                            document.getElementById('content')?.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'start',
                                            });
                                        }, 100);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='flex flex-col gap-4 justify-center items-center w-full text-center'>
                        <h1 className="font-display font-bold text-2xl sm:text-4xl md:text-5xl">
                            <HighlightText>Már van</HighlightText>
                            <br/>
                            DOQDrive Next
                            <br/>
                            regisztrációd?
                        </h1>
                        <b className="text-white font-display font-black block text-xs sm:text-lg md:text-xl">
                            Jelezd a Fürge Diák felé, hogy készen állsz a digitális ügyintézésre.
                        </b>
                        <p className="font-display text-white max-w-md mx-auto md:px-16 text-[0.7rem] sm:text-sm md:text-lg">
                            Ehhez jelentkezz be a furgediak.hu weboldalon, a szerződéskötés és digitális ügyintézés
                            <br/>
                            blokkban add meg a DOQDrive azonosítódat és nyilatkozz az elektronikus eljárások
                            <br/>
                            elfogadásáról.
                            <br/>
                            <br/>
                            Ezután a DOQ azonosítód beérkezik a Fürge Diák rendszerébe, és ettől a pillanattól tudsz
                            <br/>
                            digitálisan aláírni Fürgés diákmunkához kapcsolódó dokumentumot.
                        </p>
                        <b className="text-white font-display font-black block text-xs sm:text-lg md:text-xl">
                            Elakadtál a digitális aláírás folyamatában?
                        </b>
                        <p className="font-display text-white max-w-md mx-auto md:px-16 text-[0.7rem] sm:text-sm md:text-lg">
                            Kattints és részletesen megmutatjuk hogyan tudsz pár lépésben elektronikusan
                            <br/>
                            dokumentumot aláírni és beküldeni Iskolaszövetkezetünknek!
                        </p>

                        {/* Buttons for App Links */}
                        <Link
                            className="font-display px-4 py-2 gradient-button text-white rounded-xl shadow-2xl hover:shadow-xl 
                                            hover:scale-105 transform transition duration-300 ease-in-out hover:ring-8 ring-white/20
                                            whitespace-nowrap text-[0.8rem] sm:text-sm md:text-lg"
                            to={'/digitalis-ugyintezes-utmutato-diakoknak'}
                        >
                            Útmutató
                        </Link>
                    </div>

                    <div className="flex flex-col xl:flex-row gap-4 justify-center px-4 py-8">
                        {/* Left Section: Text */}
                        <div className="flex flex-col gap-8 justify-center items-center w-full xl:w-1/2 text-center">
                            <h1 className="font-display text-xl sm:text-2xl md:text-3xl font-bold">
                                Ha mindennel elkészültél...
                            </h1>
                            <h1 className="font-display font-bold text-2xl sm:text-4xl md:text-5xl">
                                Élvezd a digitális
                                <br />
                                ügyintézés által
                                <br />
                                <span
                                    className="relative inline-block"
                                    style={{
                                        backgroundImage: 'url(/assets/images/artifacts/felszabadult_idot.svg)',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'center',
                                        backgroundSize: 'cover',
                                        padding: '5px 10px', 
                                        display: 'inline-block',
                                    }}
                                >
                                    felszabadult időt!
                                </span>
                            </h1>

                            {/* Registration Button */}
                            <Link 
                                to={'/diakmunka'}
                                className="font-display px-4 py-2 gradient-button text-white rounded-xl shadow-2xl hover:shadow-xl 
                                                hover:scale-105 transform transition duration-300 ease-in-out hover:ring-8 ring-white/20
                                                whitespace-nowrap text-[0.8rem] sm:text-sm md:text-lg"
                            >
                                Nézz körül diákmunkáink között!
                            </Link>

                            <div className="flex flex-row gap-4 justify-center">
                            {/* Facebook Button */}
                                <SocialCard to="https://www.facebook.com/furgediak" icon="/assets/images/doq_landing/11.svg">
                                    Kövess minket<br />Facebookon!
                                </SocialCard>

                                {/* Instagram Button */}
                                <SocialCard to="https://www.instagram.com/furge.diak" icon="/assets/images/doq_landing/12.svg">
                                    Kövess minket<br />Instagramon!
                                </SocialCard>
                            </div>
                        </div>

                        {/* Right Section: Image */}
                        <div className="flex justify-center items-center">
                            <img
                                src="/assets/images/bgPicture2.png"
                                alt="Background Picture"
                                className="size-[320px] md:size-[560px] object-contain"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}